import { environment } from '@app/environments/environment';
import {
    ApplicationInsights,
    DistributedTracingModes,
    ITelemetryItem
} from '@microsoft/applicationinsights-web';
import * as Sentry from '@sentry/angular-ivy';
import { Contexts, SeverityLevel } from '@sentry/types';
import { beforeSentrySend, beforeSentryBreadcrumb } from '@studio/monitoring/browser/monitoring.utils';
import { Logger } from '@studio/utils/logger';

export enum TrackerName {
    SaveCreative = 'Save Creative',
    PublishChanges = 'Publish Changes',
    WeightCalculation = 'Weight Calculation'
}

export class TrackerService {
    public static appInsights: ApplicationInsights;
    private static logger = new Logger('TrackerService');

    static initAI(): void {
        if (!environment.appInsights.enabled) {
            return;
        }

        this.logger.verbose(`Init AppInsights in {${environment.stage}}`);

        TrackerService.appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: environment.appInsights.instrumentationKey,
                disableCookiesUsage: true,
                enableAutoRouteTracking: true,
                disableExceptionTracking: true,
                disableFetchTracking: false,
                correlationHeaderExcludedDomains: ['sentry'], // prevents sentry CORS errors
                samplingPercentage: environment.appInsights.samplingPercentage,
                distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
                excludeRequestFromAutoTrackingPatterns: ['aptrinsic'],
                enableCorsCorrelation: true,
                enableRequestHeaderTracking: true,
                enableResponseHeaderTracking: true
            }
        });
        TrackerService.appInsights.loadAppInsights();
    }

    static initSentry(): void {
        this.logger.verbose(
            `Init Sentry in {${environment.stage}}, enabled {${environment.sentry.enabled}}`
        );
        const release = this.getSentryRelease();
        Sentry.init({
            dsn: 'https://4c933b9c581e4694b9d44d5092e5649c@o277179.ingest.sentry.io/1496801',
            enabled: environment.sentry.enabled,
            replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
            replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate,
            integrations: environment.sentry.integrations,
            environment: environment.stage,
            release,
            maxBreadcrumbs: 50,
            beforeSend: beforeSentrySend,
            beforeBreadcrumb: beforeSentryBreadcrumb
        });
    }

    static setUser(username: string): void {
        Sentry.setUser({ username });
        const telemetryInitializer = (envelope: ITelemetryItem): void => {
            if (!envelope.data) {
                return;
            }
            envelope.data.account = username;
        };
        TrackerService.appInsights?.addTelemetryInitializer(telemetryInitializer);

        if (!newrelic) {
            return;
        }
        newrelic.setCustomAttribute('account', username);
    }

    static trackEvent(name: TrackerName | string, data?: any): void {
        if (!TrackerService.appInsights) {
            return;
        }
        TrackerService.appInsights.trackEvent({
            name,
            properties: data
        });

        if (!newrelic) {
            return;
        }
        newrelic.addPageAction(name, data);
    }

    static startTracking(name: TrackerName | string): void {
        if (!TrackerService.appInsights) {
            return;
        }
        TrackerService.appInsights.startTrackEvent(name);
    }

    static finishTracking(trackerName: TrackerName | string): void {
        if (!TrackerService.appInsights) {
            return;
        }

        TrackerService.appInsights.stopTrackEvent(trackerName);
    }

    static captureException(error: any, context?: Record<string, string | number>): void {
        Sentry.withScope(scope => {
            if (context) {
                scope.setExtra('context', context);
            }
            Sentry.captureException(error);
        });

        if (!newrelic) {
            return;
        }
        newrelic.noticeError(error, context);
    }

    static captureMessage(message: any, severity?: SeverityLevel, contexts?: Contexts): void {
        const context = {
            level: severity,
            contexts
        };
        Sentry.captureMessage(message, context);

        if (!newrelic) {
            return;
        }
        newrelic.interaction().setAttribute(message, context);
    }

    static captureMetric(metricName: string, value: number): void {
        if (!TrackerService.appInsights) {
            return;
        }
        TrackerService.appInsights.trackMetric({ name: metricName, average: value });
    }

    private static getSentryRelease(): string {
        if (environment.sandbox && environment.build.branch !== 'master') {
            return `PR-${environment.build.branch}`;
        }

        return environment.production ? environment.build.version : 'local';
    }
}

(typeof window !== 'undefined' ? (window as any) : (global as any)).TrackerService = TrackerService;
